import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Download, ShoppingBag, Check, Clock, Star, ArrowRight, ExternalLink, Bell, Package } from "lucide-react";
import { PageSEO } from "@/components/SEO";
import { useApiGet } from "@/hooks/useApi";
import CheckoutModal from "@/components/CheckoutModal";
import FreeDownloadModal from "@/components/FreeDownloadModal";
import WaitlistModal from "@/components/WaitlistModal";
import PhysicalCheckoutModal from "@/components/PhysicalCheckoutModal";
import { toast } from "sonner";

const TIER_LABELS = { 1: "Free", 2: "Essentials", 3: "Premium", 4: "Signature" };
const TIER_COLORS = { 1: "var(--ll-green)", 2: "var(--ll-accent)", 3: "var(--ll-text-secondary)", 4: "var(--ll-text)" };

const PriceDisplay = ({ product, currency = "ZAR" }) => {
  const priceZar = product.launch_price_zar || product.price_zar;
  if (priceZar === 0) return <span className="font-serif text-xl font-semibold" style={{ color: "var(--ll-green)" }}>Free</span>;
  if (currency === "USD" && product.price_usd > 0) {
    return <span className="font-serif text-xl font-semibold" style={{ color: "var(--ll-text)" }}>${product.price_usd}</span>;
  }
  return (
    <span>
      {product.launch_price_zar && product.price_zar > product.launch_price_zar && (
        <span className="text-xs line-through mr-1.5" style={{ color: "var(--ll-text-muted)" }}>R{product.price_zar}</span>
      )}
      <span className="font-serif text-xl font-semibold" style={{ color: "var(--ll-text)" }}>R{priceZar}</span>
    </span>
  );
};

const ProductCard = ({ product, currency, onAction }) => {
  const isComingSoon = product.status === "coming_soon";
  const isWaitlist = product.waitlist && isComingSoon;
  const isFree = (product.price_zar || 0) === 0;
  const isPhysical = product.is_physical;

  return (
    <div data-testid={`shop-product-${product.id}`}
      className="group flex flex-col rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1"
      style={{ background: "white", border: "1px solid var(--ll-border)", opacity: isComingSoon && !isWaitlist ? 0.7 : 1 }}>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[9px] tracking-wider uppercase px-2.5 py-1 rounded-full"
            style={{ background: isFree ? "rgba(163,184,153,0.2)" : "var(--ll-bg-secondary)", color: isFree ? "var(--ll-green)" : "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            {isFree ? "Free" : product.type}
          </span>
          <div className="flex items-center gap-1.5">
            {isPhysical && (
              <span className="text-[9px] tracking-wider uppercase px-2.5 py-1 rounded-full flex items-center gap-1"
                style={{ background: "rgba(163,184,153,0.15)", color: "var(--ll-green)", fontFamily: "'Outfit', sans-serif" }}>
                <Package size={9} /> Physical
              </span>
            )}
            {isComingSoon && (
              <span className="text-[9px] tracking-wider uppercase px-2.5 py-1 rounded-full flex items-center gap-1"
                style={{ background: "rgba(247,208,70,0.15)", color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}>
                <Clock size={9} /> Coming Soon
              </span>
            )}
          </div>
        </div>

        <h3 className="font-serif text-lg font-medium mb-2" style={{ color: "var(--ll-text)" }}>{product.name}</h3>
        <p className="text-xs leading-relaxed mb-4 flex-1" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{product.description}</p>

        <ul className="space-y-1.5 mb-5">
          {product.features?.slice(0, 3).map((f) => (
            <li key={`${product.id}-${f}`} className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              <Check size={10} style={{ color: "var(--ll-green)" }} /> {f}
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between mt-auto pt-3" style={{ borderTop: "1px solid var(--ll-border)" }}>
          <PriceDisplay product={product} currency={currency} />
          {isWaitlist ? (
            <button data-testid={`shop-cta-${product.id}`} onClick={() => onAction(product)}
              className="rounded-full px-4 py-2 text-xs font-medium transition-all duration-300 hover:-translate-y-0.5 flex items-center gap-1.5"
              style={{ background: "var(--ll-accent)", color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>
              <Bell size={12} /> Join Waitlist
            </button>
          ) : (
            <button data-testid={`shop-cta-${product.id}`} onClick={() => !isComingSoon && onAction(product)} disabled={isComingSoon}
              className="rounded-full px-4 py-2 text-xs font-medium transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: isFree ? "var(--ll-green)" : "var(--ll-cta)", color: isFree ? "white" : "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>
              {isComingSoon ? "Coming Soon" : product.cta || "Get Now"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ShopPage = () => {
  const { data: productsData } = useApiGet("/products");
  const { data: categoriesData } = useApiGet("/shop/categories");
  const [activeCategory, setActiveCategory] = useState("all");
  const [currency, setCurrency] = useState("ZAR");
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [freeDownloadProduct, setFreeDownloadProduct] = useState(null);
  const [waitlistProduct, setWaitlistProduct] = useState(null);
  const [physicalProduct, setPhysicalProduct] = useState(null);

  const products = productsData?.products || [];
  const categories = categoriesData?.categories || [];

  // Auto-detect currency on mount
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isAfrica = tz?.startsWith("Africa/");
      setCurrency(isAfrica ? "ZAR" : "USD");
    } catch { setCurrency("ZAR"); }
  }, []);

  const filtered = activeCategory === "all" ? products : products.filter((p) => p.category === activeCategory);
  const tiers = [1, 2, 3, 4];

  const handleAction = (product) => {
    if (product.waitlist && product.status === "coming_soon") {
      setWaitlistProduct(product);
    } else if (product.is_physical) {
      setPhysicalProduct(product);
    } else if ((product.price_zar || 0) === 0) {
      setFreeDownloadProduct(product);
    } else if (product.type === "course" && product.id === "course-burnout-to-balance-in-action") {
      setCheckoutProduct({ ...product, price: product.price_zar });
    } else {
      setCheckoutProduct({ ...product, price: product.launch_price_zar || product.price_zar });
    }
  };

  return (
    <main data-testid="shop-page" className="pt-20">
      <PageSEO title="Shop — Digital Wellness Products" description="Digital journals, planners, workbooks, and courses for mental wellness. Free resources to premium transformation tools. The Lemonade Living Company." path="/shop" />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>The Shop</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1]" style={{ color: "var(--ll-text)" }}>
                Tools for Your{" "}
                <em className="italic" style={{ color: "var(--ll-text-secondary)" }}>Transformation</em>
              </h1>
            </div>
            {/* Currency toggle */}
            <div className="flex items-center gap-2 p-1 rounded-full" style={{ background: "var(--ll-bg-secondary)" }}>
              {["ZAR", "USD"].map((c) => (
                <button key={c} onClick={() => setCurrency(c)} data-testid={`currency-${c}`}
                  className="px-4 py-1.5 rounded-full text-xs font-medium transition-all"
                  style={{ background: currency === c ? "white" : "transparent", color: currency === c ? "var(--ll-text)" : "var(--ll-text-muted)", boxShadow: currency === c ? "0 1px 3px rgba(0,0,0,0.06)" : "none", fontFamily: "'Outfit', sans-serif" }}>
                  {c === "ZAR" ? "R (ZAR)" : "$ (USD)"}
                </button>
              ))}
            </div>
          </div>

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 mb-10">
            <button onClick={() => setActiveCategory("all")} className="rounded-full px-5 py-2 text-xs font-medium transition-all"
              style={{ background: activeCategory === "all" ? "var(--ll-cta)" : "transparent", color: activeCategory === "all" ? "var(--ll-cta-text)" : "var(--ll-text-muted)", border: `1px solid ${activeCategory === "all" ? "var(--ll-cta)" : "var(--ll-border)"}`, fontFamily: "'Outfit', sans-serif" }}>
              All Products
            </button>
            {categories.map((cat) => (
              <button key={cat.id} data-testid={`shop-filter-${cat.id}`} onClick={() => setActiveCategory(cat.id)}
                className="rounded-full px-5 py-2 text-xs font-medium transition-all"
                style={{ background: activeCategory === cat.id ? "var(--ll-cta)" : "transparent", color: activeCategory === cat.id ? "var(--ll-cta-text)" : "var(--ll-text-muted)", border: `1px solid ${activeCategory === cat.id ? "var(--ll-cta)" : "var(--ll-border)"}`, fontFamily: "'Outfit', sans-serif" }}>
                {cat.name}
              </button>
            ))}
          </div>

          {/* Products by tier */}
          {activeCategory === "all" ? (
            tiers.map((tier) => {
              const tierProducts = filtered.filter((p) => p.tier === tier);
              if (tierProducts.length === 0) return null;
              return (
                <div key={tier} className="mb-14">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="w-1.5 h-6 rounded-full" style={{ background: TIER_COLORS[tier] }} />
                    <h2 className="font-serif text-xl font-medium" style={{ color: "var(--ll-text)" }}>{TIER_LABELS[tier]}</h2>
                    <span className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>{tierProducts.length} products</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {tierProducts.map((p) => <ProductCard key={p.id} product={p} currency={currency} onAction={handleAction} />)}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filtered.map((p) => <ProductCard key={p.id} product={p} currency={currency} onAction={handleAction} />)}
            </div>
          )}

          {filtered.length === 0 && (
            <p className="text-center py-16 text-sm" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>No products in this category yet.</p>
          )}
        </div>
      </section>

      <FreeDownloadModal
        open={!!freeDownloadProduct}
        onClose={() => setFreeDownloadProduct(null)}
        product={freeDownloadProduct}
        onCheckout={(p) => { setFreeDownloadProduct(null); setCheckoutProduct(p); }}
      />
      <WaitlistModal
        open={!!waitlistProduct}
        onClose={() => setWaitlistProduct(null)}
        product={waitlistProduct}
      />
      <PhysicalCheckoutModal
        open={!!physicalProduct}
        onClose={() => setPhysicalProduct(null)}
        product={physicalProduct}
      />
      {checkoutProduct && (
        <CheckoutModal product={checkoutProduct} open={!!checkoutProduct} onClose={() => setCheckoutProduct(null)} />
      )}
    </main>
  );
};

export default ShopPage;
